import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable } from "mobx";
import { routeLocationCashbackClaimPost, routeLocationPaddleIncompleteCheckoutGet } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { realCommerceRequest } from "../../../utils/realCommerceRequest.js";
import { PaddleTransaction } from "../models/paddleTransaction.js";
import { Product } from "../models/product.js";
var CheckoutStep = /*#__PURE__*/ function(CheckoutStep) {
    CheckoutStep[CheckoutStep["Initial"] = 0] = "Initial";
    CheckoutStep[CheckoutStep["ProductReady"] = 1] = "ProductReady";
    CheckoutStep[CheckoutStep["VendorReady"] = 2] = "VendorReady";
    CheckoutStep[CheckoutStep["IdentificationSubmitted"] = 3] = "IdentificationSubmitted";
    CheckoutStep[CheckoutStep["PaymentReady"] = 4] = "PaymentReady";
    CheckoutStep[CheckoutStep["PaymentProcessing"] = 5] = "PaymentProcessing";
    CheckoutStep[CheckoutStep["PaymentCompleted"] = 6] = "PaymentCompleted";
    /**
     * When the transaction got retried x times to complete but did not yet
     * finish after the x.th retry, show confirmation.
     */ CheckoutStep[CheckoutStep["PaymentPendingConfirmation"] = 7] = "PaymentPendingConfirmation";
    return CheckoutStep;
}(CheckoutStep || {});
class CheckoutV2Store {
    setInvalidCouponNotice(message) {
        this.invalidCouponNotice = message;
    }
    setCustomerCenterUrl(url) {
        this.customerCenterUrl = url || undefined;
    }
    get loading() {
        return this.step < 2 || this.busyRecover || this.busyCashbackClaim;
    }
    /**
     * 0-indexed current progress position for the 4-step progress indicator:
     * 0 = plan selected (always reached),
     * 1 = your details (after identification applied),
     * 2 = payment (after payment),
     * 3 = final confirmation / license & download (when view switched to success).
     */ get currentStep() {
        if (this.view === "success") {
            return 3;
        }
        if (this.step >= 6) {
            return 3;
        }
        if (this.step >= 4) {
            return 2;
        }
        if (this.step >= 3) {
            return 2;
        }
        return 1;
    }
    constructor(rootStore){
        this.busyRecover = false;
        this.busyCashbackClaim = false;
        this.step = 0;
        this.view = "checkout";
        this.hasCheckoutFormInteraction = false;
        /**
     * Shared form-state bag so components can seed values (e.g. a coupon
     * captured by the order summary) before the identification form reads
     * them on submit.
     */ this.formValues = {};
        this.startTransaction = flow(function*(params, recovered) {
            this.setStep(0);
            this.setHasCheckoutFormInteraction(false);
            this.product = yield Product.loadProduct(params, this);
            // Start product transaction (currently only Paddle is supported)
            const { name } = this.product.data.productPlan.product.ecommerceSystem;
            switch(name.toLowerCase()){
                case "paddle":
                    this.transaction = new PaddleTransaction(this.product, recovered);
                    break;
                default:
                    throw new Error(`${name} transactions not yet implemented.`);
            }
            this.setStep(1);
            // Load external vendor files
            yield this.transaction.loadVendorScripts();
            this.setStep(2);
        });
        /**
     * Recover incomplete checkout from a given UUID and automatically start the transaction.
     *
     * @param uuid
     */ this.recoverIncompleteCheckout = flow(function*(uuid) {
            this.busyRecover = true;
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutGet,
                    params: {
                        uuid
                    }
                });
                // Start transaction with the known product
                const { checkout: { product: { id: productId }, productPlan: { id: productPlanId }, currency } } = result;
                this.startTransaction({
                    productId,
                    productPlanId,
                    currency
                }, result);
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.busyRecover = false;
            }
        });
        /**
     * Claim cashback for an already-completed order via the cashback-token query parameter.
     * Used when the user returns from an email link after the follow-up period.
     */ this.claimCashback = flow(function*(cashbackToken) {
            this.busyCashbackClaim = true;
            try {
                const { checkout: { cashbackPromotion } } = yield realCommerceRequest({
                    location: routeLocationCashbackClaimPost,
                    request: {
                        checkout: {
                            cashbackToken
                        }
                    }
                });
                this.claimedCashback = cashbackPromotion;
            } catch (e) {
                this.claimedCashback = new RealCommerceError(e);
            } finally{
                this.busyCashbackClaim = false;
            }
        });
        this.rootStore = rootStore;
    }
    setStep(step) {
        this.step = step;
    }
    setView(view) {
        this.view = view;
    }
    setHasCheckoutFormInteraction(value) {
        this.hasCheckoutFormInteraction = value;
    }
    markCheckoutFormInteraction() {
        if (!this.hasCheckoutFormInteraction) {
            this.hasCheckoutFormInteraction = true;
        }
    }
    setFatalError(error) {
        this.fatalError = error;
    }
}
_ts_decorate([
    observable
], CheckoutV2Store.prototype, "busyRecover", void 0);
_ts_decorate([
    observable
], CheckoutV2Store.prototype, "busyCashbackClaim", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], CheckoutV2Store.prototype, "claimedCashback", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], CheckoutV2Store.prototype, "fatalError", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Number)
], CheckoutV2Store.prototype, "step", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof CheckoutV2View === "undefined" ? Object : CheckoutV2View)
], CheckoutV2Store.prototype, "view", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Product === "undefined" ? Object : Product)
], CheckoutV2Store.prototype, "product", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Transaction === "undefined" ? Object : Transaction)
], CheckoutV2Store.prototype, "transaction", void 0);
_ts_decorate([
    observable
], CheckoutV2Store.prototype, "hasCheckoutFormInteraction", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Record === "undefined" ? Object : Record)
], CheckoutV2Store.prototype, "formValues", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], CheckoutV2Store.prototype, "invalidCouponNotice", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setInvalidCouponNotice", null);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], CheckoutV2Store.prototype, "customerCenterUrl", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setCustomerCenterUrl", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], CheckoutV2Store.prototype, "loading", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Number),
    _ts_metadata("design:paramtypes", [])
], CheckoutV2Store.prototype, "currentStep", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Number
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setStep", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof CheckoutV2View === "undefined" ? Object : CheckoutV2View
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setView", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setHasCheckoutFormInteraction", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "markCheckoutFormInteraction", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], CheckoutV2Store.prototype, "setFatalError", null);
export { CheckoutV2Store, CheckoutStep };
